import { usePlatformLocationFeatureV1QueryUpdaters } from '../query-updaters';
import { CreateOrUpdateFeatureNotificationUserActionIO } from '../types';
import {
  UsePlatformLocationFeatureV1MutationEndpointArgs,
  usePlatformLocationFeatureV1Mutation,
} from './use-platform-location-feature-v1-mutation';

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `CreateOrUpdateFeatureNotificationUserAction` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useCreateOrUpdateFeatureNotificationUserActionMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends CreateOrUpdateFeatureNotificationUserActionIO['input'] = CreateOrUpdateFeatureNotificationUserActionIO['input']
>({
  options,
  // optimisticUpdate = false,
  ...args
}: UsePlatformLocationFeatureV1MutationEndpointArgs<
  'CreateOrUpdateFeatureNotificationUserAction',
  E,
  C | undefined,
  OtherOptions,
  RequestOverride
> = {}) => {
  const { invalidateQueries } = usePlatformLocationFeatureV1QueryUpdaters();
  return usePlatformLocationFeatureV1Mutation<
    'CreateOrUpdateFeatureNotificationUserAction',
    E,
    MutationContext<C>,
    OtherOptions,
    RequestOverride
  >({
    endpointName: 'CreateOrUpdateFeatureNotificationUserAction',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        // Implement optimistic updates here

        return {
          // Add custom context type here (if exists in `MutationContext`)
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => {
        // Implement query updates or invalidation for affected queries here
        invalidateQueries({
          endpointName: 'CheckFeatureNotificationStatus',
          queryFilters: {
            predicate: ({ queryKey }) => {
              const cachedRequest = queryKey[2];
              return (
                cachedRequest.notificationFeature === request.notificationFeature &&
                !!request.sourceEnum?.some((sourceEnum) => sourceEnum === cachedRequest.sourceEnum)
              );
            },
            exact: false,
          },
        });
        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        // Revert optimistic update here (if implemented)

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
